<template lang="pug">
    section.abonement
        el-row
            el-collapse(accordion v-if="blocks && blocks.length")
                transition-group(name="slide-decent-vertical")
                    el-collapse-item(
                        v-for="block in blocks" :key="block.key"
                        v-if="showToggleButtons || checkConditions(block)"
                        :name="block.key"
                        :disabled="block.empty"
                    )
                        template(slot="title")
                            .flex-row
                                .checkbox(v-if="showToggleButtons")
                                    el-checkbox(
                                        :value="getSectionVisibility(block.key)"
                                        @click.native.stop.prevent="toggleSectionVisibility(block.key)"
                                    )
                                FontAwesomeIcon.icon(v-if="block.icon" :icon="block.icon")
                                FontAwesomeIcon.icon(v-else icon="fa-regular fa-square")
                                .collapse-title {{ block.name }}<br>
                                    span.small {{ block.key }}
                                .grow
                        transition-group(name="slide-decent-vertical")
                            .row(
                                v-for="(group, i) in block.groups" :key="`${block.key}:${i}`"
                                v-if="checkConditions(group)"
                            )
                                transition-group.flex(name="slide-decent-vertical")
                                    SettingField.setting-field(
                                        v-for="field in group.fields" :key="`${block.key}:${i}:${field.key}`"
                                        v-if="checkConditions(field)"
                                        :field="field"
                                        :setting-values="settingValues"
                                    )

</template>

<script>

import _ from 'lodash'
import uniqid from 'uniqid'

import store from '@/store'
import pinia from '@/pinia'
import { useAuthStore, useSettingsStore, useSetupStore } from '@/stores'
import { sectionIsVisible } from '@/utils/sectionVisibility'

import SettingField from './Partials/SettingField'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
    name: 'AbonementSections',
    components: {
        SettingField,
        FontAwesomeIcon
    },
    computed: {
        settings() {
            const settings = _.get(this.form, 'config.template.settings') || []
            return settings.find(el => el.key === 'sections')
        },
        sections() {
            return _.get(this.form, 'config.template.sections') || []
        },
        blocks() {
            let blocks = _.get(this.settings, 'blocks') || []
            blocks = _.cloneDeep(blocks)

            if (this.showToggleButtons) {
                let sections = []
                this.sections.forEach(section => {
                    const block = blocks.find(b => b.key === section.key)
                    if (block) {
                        if (!block.name || !block.icon) {
                            const section = this.sections.find(s => s.key === block.key)
                            if (!block.name) block.name = _.get(section, 'name')
                            if (!block.icon) block.icon = _.get(section, 'icon')
                        }

                        sections.push(block)
                    } else {
                        sections.push({
                            key: section.key,
                            name: section.name,
                            icon: section.icon,
                            empty: true,
                        })
                    }
                })

                return sections.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
            } else {
                blocks.forEach(block => {
                    if (!block.name || !block.icon) {
                        const section = this.sections.find(s => s.key === block.key)
                        if (!block.name) block.name = _.get(section, 'name')
                        if (!block.icon) block.icon = _.get(section, 'icon')
                    }
                })

                return blocks.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
            }
        },
        showToggleButtons() {
            return this.isSuperUser || useAuthStore().isImpersonatedBySuperUser;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        isOwner() {
            return useAuthStore().isOwner;
        },
        form() {
            return useSettingsStore().form;
        },
        settingValues() {
            return useSettingsStore().values;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        enabledFeatures() {
            return useSetupStore().enabledFeatures;
        }
    },
    methods: {
        getSectionVisibility(key) {
            return sectionIsVisible(this.sections.find(section => section.key === key), this.settingValues)
        },
        toggleSectionVisibility(key) {
            const fieldKey = `section:${key}:visibility`
            const template_key = this.form.config.template.id

            const index = this.form.settings.findIndex(function (item, i) {
                return item.key.toString() === fieldKey
                    && item.template_key.toString() === template_key
            });

            let value = !this.getSectionVisibility(key)

            if (index >= 0) {
                useSettingsStore().setFormSetting({ index, value });
            } else {
                useSettingsStore().addFormSetting({
                    id: uniqid('new:'),
                    key: fieldKey,
                    template_key,
                    value,
                })
            }
        },
        checkConditions(setting) {
            let valid = true

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            // eslint-disable-next-line no-unused-vars
            const enabledExtensions = this.enabledExtensions;
            // eslint-disable-next-line no-unused-vars
            const enabledFeatures = this.enabledFeatures;
            try {
                const conditions = setting.conditions
                if (conditions && conditions.length) {
                    conditions.forEach(o => {
                        if (valid === false) return

                        const { type, condition } = o
                        switch (type) {
                            case 'eval':
                                valid = eval(condition)
                                break;

                            default:
                                valid = false
                        }
                    })
                }
            } catch (error) {
                // eslint-disable-next-line
                console.error(error);
                valid = false
            }

            return valid
        }
    },
    beforeRouteEnter(to, from, next) {
        let cms = useSetupStore(pinia).activeCms;

        next(vm => {
            const settings = _.get(vm.form, 'config.template.settings')
                || _.get(vm.activeCms, 'config.template.settings')
                || []

            if (!settings.find(el => el.key === 'sections')) {
                next({ name: 'settings-form' });
            } else {
                next();
            }
        })
    }
}
</script>

<style lang="sass" scoped>
.row
    display: flex
    margin-bottom: 12px
    & > span.flex
        width: 100%
        display: flex
        flex-wrap: wrap
        row-gap: 20px

.setting-field
    margin-right: 20px

.col-right
    img
        width: 90%

.el-main.main-slot .el-form .el-card .el-card__body section > .el-row > .el-col-lg-12:first-child:before
    display: none

.flex-row
    gap: 10px
    width: 100%
    padding-right: 10px
    justify-content: center
    align-items: center

.collapse-title
    line-height: 1
    .small
        font-size: 0.8em
        color: #bbb

.icon
    height: 18px
    width: 18px
    padding: 7px
    // color: #fff
    // background: #474747
    // border: 1px solid #575757
    // border-radius: 2px
</style>
